/**
 * Single entry for community operations — mock or admin-community-api.
 *
 * Mock mode routes to communityMockStore (full offline parity).
 * API mode routes to communityAdmin (admin-community-api-serverless, /api/v1/community).
 * The acting user id passed here is only used by mock mode; the real API derives
 * identity from the JWT.
 */

import { communityMockStore } from './communityMockStore';
import { getCommunityDataSource, DATA_SOURCE_MOCK, DATA_SOURCE_API } from '../../utils/communityDataSource';
import * as communityAdmin from '../../api/communityAdmin';

function isMockMode() {
  return getCommunityDataSource() === DATA_SOURCE_MOCK;
}

export function getDataSource() {
  return getCommunityDataSource();
}

/* ---- Threads CRUD ---- */

export async function getThreadById(id, opts = {}) {
  if (isMockMode()) return communityMockStore.getThread(id, opts.include_deleted, opts.acting_user_id);
  return communityAdmin.getThreadById(id, opts);
}

export async function postThread(payload, actingUserId) {
  if (isMockMode()) return communityMockStore.postThread(payload, actingUserId);
  return communityAdmin.postThread(payload, actingUserId);
}

export async function patchThread(id, payload, actingUserId) {
  if (isMockMode()) return communityMockStore.patchThread(id, payload, actingUserId, true);
  return communityAdmin.patchThread(id, payload);
}

export async function deleteThread(id) {
  if (isMockMode()) return communityMockStore.deleteThread(id, null, true);
  return communityAdmin.deleteThread(id);
}

/* ---- Feeds & discovery ---- */

export async function getThreadsFeed(params) {
  if (isMockMode()) return communityMockStore.getThreadsFeed(params);
  return communityAdmin.getThreadsFeed(params);
}

export async function getThreadsNearby(params, actingUserId) {
  if (isMockMode()) return communityMockStore.getThreadsNearby(params, actingUserId);
  return communityAdmin.getThreadsNearby(params);
}

export async function getThreadsGlobal(params) {
  if (isMockMode()) return communityMockStore.getThreadsGlobal(params);
  return communityAdmin.getThreadsGlobal(params);
}

export async function getMyThreads(params, actingUserId) {
  if (isMockMode()) return communityMockStore.getMyThreads(params, actingUserId);
  return communityAdmin.getMyThreads(params);
}

export async function getThreadsTrending(params) {
  if (isMockMode()) return communityMockStore.getThreadsTrending(params);
  return communityAdmin.getThreadsTrending(params);
}

export async function getThreadsGps(params) {
  if (isMockMode()) return communityMockStore.getThreadsGps(params);
  return communityAdmin.getThreadsGps(params);
}

export async function getThreadsByTag(params) {
  if (isMockMode()) return communityMockStore.getThreadsByTag(params);
  return communityAdmin.getThreadsByTag(params);
}

export async function postNearbyUnreadCount(payload, actingUserId) {
  if (isMockMode()) return communityMockStore.postNearbyUnreadCount(payload, actingUserId);
  return communityAdmin.postNearbyUnreadCount(payload);
}

/* ---- Comments ---- */

export async function getComments(threadId, params) {
  if (isMockMode()) return communityMockStore.getComments(threadId, params);
  return communityAdmin.getComments(threadId, params);
}

export async function postComment(threadId, payload, actingUserId) {
  if (isMockMode()) return communityMockStore.postComment(threadId, payload, actingUserId);
  return communityAdmin.postComment(threadId, payload, actingUserId);
}

export async function patchComment(commentId, payload, actingUserId) {
  if (isMockMode()) return communityMockStore.patchComment(commentId, payload, actingUserId, true);
  return communityAdmin.patchComment(commentId, payload);
}

export async function deleteComment(commentId) {
  if (isMockMode()) return communityMockStore.deleteComment(commentId, null, true);
  return communityAdmin.deleteComment(commentId);
}

/* ---- Votes ---- */

export async function postVote(threadId, value, actingUserId) {
  if (isMockMode()) return communityMockStore.postVote(threadId, value, actingUserId);
  return communityAdmin.postVote(threadId, value);
}

export async function deleteVote(threadId, actingUserId) {
  if (isMockMode()) return communityMockStore.deleteVote(threadId, actingUserId);
  return communityAdmin.deleteVote(threadId);
}

/* ---- Likes (deprecated) ---- */

export async function postLike(threadId, userId) {
  if (isMockMode()) return communityMockStore.postLike(threadId, userId);
  return communityAdmin.postLike(threadId, userId);
}

export async function deleteLike(threadId, userId) {
  if (isMockMode()) return communityMockStore.deleteLike(threadId, userId);
  return communityAdmin.deleteLike(threadId, userId);
}

export async function getLikesForThread(threadId) {
  if (isMockMode()) return communityMockStore.getLikesForThread(threadId);
  const e = new Error('Likes list is not available on the community API (no GET .../likes endpoint).');
  e.status = 501;
  throw e;
}

/* ---- Reports ---- */

export async function postReport(threadId, payload, reporterUserId) {
  if (isMockMode()) return communityMockStore.postReport(threadId, payload, reporterUserId);
  return communityAdmin.postReport(threadId, payload, reporterUserId);
}

export async function getReports(params) {
  if (isMockMode()) return communityMockStore.getReports(params);
  return communityAdmin.getReports(params);
}

/* ---- Views ---- */

export async function postThreadView(threadId, userId) {
  if (isMockMode()) return communityMockStore.postThreadView(threadId, userId);
  return communityAdmin.postThreadView(threadId, userId);
}

/* ---- Boards ---- */

export async function postBoard(payload, actingUserId) {
  if (isMockMode()) return communityMockStore.postBoard(payload, actingUserId);
  return communityAdmin.postBoard(payload);
}

export async function getBoards(params) {
  if (isMockMode()) return communityMockStore.getBoards(params);
  return communityAdmin.getBoards(params);
}

export async function getBoardById(boardId) {
  if (isMockMode()) return communityMockStore.getBoardById(boardId);
  return communityAdmin.getBoardById(boardId);
}

export async function getThreadsByBoard(boardId, params) {
  if (isMockMode()) return communityMockStore.getThreadsByBoard(boardId, params);
  return communityAdmin.getThreadsByBoard(boardId, params);
}

/* ---- Settings ---- */

export async function updateReadDistance(payload, actingUserId) {
  if (isMockMode()) return communityMockStore.updateReadDistance(payload, actingUserId);
  return communityAdmin.updateReadDistance(payload);
}

/* ---- Aggregates ---- */

export async function getOverviewStats() {
  if (isMockMode()) return communityMockStore.getOverviewStats();
  return communityAdmin.getOverviewStats();
}

export async function getRegionalThreadsForMap() {
  if (isMockMode()) return communityMockStore.getRegionalThreadsForMap();
  return communityAdmin.getRegionalThreadsForMap();
}

export function resetMockStore() {
  if (getCommunityDataSource() === DATA_SOURCE_MOCK) {
    communityMockStore.reset();
  }
}

export { DATA_SOURCE_MOCK, DATA_SOURCE_API };
